/**
 * Integrations module API wrapper (Wave 3.4 — live API).
 *
 * Covers: API Keys, Webhooks (+ deliveries + rotate-secret), Connections,
 * Sync Logs, and Overview stats.
 */

import { http } from './client';
import type { Schema } from './types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ApiKeyResponse = Schema<'ApiKeyResponse'>;
export type ApiKeyCreatedResponse = Schema<'ApiKeyCreatedResponse'>;
export type WebhookResponse = Schema<'WebhookResponse'>;
export type WebhookCreatedResponse = Schema<'WebhookCreatedResponse'>;
export type WebhookDeliveryResponse = Schema<'WebhookDeliveryResponse'>;
export type ConnectionResponse = Schema<'ConnectionResponse'>;
export type SyncLogResponse = Schema<'SyncLogResponse'>;
export type IntegrationsOverview = Schema<'IntegrationsOverviewResponse'>;

// ---------------------------------------------------------------------------
// API Keys
// ---------------------------------------------------------------------------

export const apiKeysApi = {
  list: () => http.get<ApiKeyResponse[]>('/v1/integrations/api-keys'),
  create: (data: { name: string; scopes?: string[]; expires_at?: string | null }) =>
    http.post<ApiKeyCreatedResponse>('/v1/integrations/api-keys', data),
  revoke: (id: string) =>
    http.post<ApiKeyResponse>(`/v1/integrations/api-keys/${id}/revoke`),
  delete: (id: string) => http.delete(`/v1/integrations/api-keys/${id}`),
  usage: () => http.get<{ total_calls: number; daily_breakdown: unknown[] }>('/v1/integrations/api-keys/usage'),
  topEndpoints: () => http.get<{ endpoints: unknown[] }>('/v1/integrations/api-keys/top-endpoints'),
};

// ---------------------------------------------------------------------------
// Webhooks
// ---------------------------------------------------------------------------

export const webhooksApi = {
  list: () => http.get<WebhookResponse[]>('/v1/integrations/webhooks'),
  create: (data: { name: string; url: string; events: string[] }) =>
    http.post<WebhookCreatedResponse>('/v1/integrations/webhooks', data),
  update: (id: string, data: { name?: string; url?: string; events?: string[]; status?: string }) =>
    http.patch<WebhookResponse>(`/v1/integrations/webhooks/${id}`, data),
  delete: (id: string) => http.delete(`/v1/integrations/webhooks/${id}`),
  test: (id: string) =>
    http.post<WebhookDeliveryResponse>(`/v1/integrations/webhooks/${id}/test`),
  rotateSecret: (id: string) =>
    http.post<WebhookCreatedResponse>(`/v1/integrations/webhooks/${id}/rotate-secret`),
  deliveries: (id: string) =>
    http.get<WebhookDeliveryResponse[]>(`/v1/integrations/webhooks/${id}/deliveries`),
};

// ---------------------------------------------------------------------------
// Connections
// ---------------------------------------------------------------------------

export const connectionsApi = {
  list: () => http.get<ConnectionResponse[]>('/v1/integrations/connections'),
  get: (provider: string) =>
    http.get<ConnectionResponse>(`/v1/integrations/connections/${provider}`),
  connect: (provider: string, data: { provider: string; category: string; config?: Record<string, unknown> }) =>
    http.post<ConnectionResponse>(`/v1/integrations/connections/${provider}/connect`, data),
  update: (provider: string, data: { config?: Record<string, unknown>; status?: string }) =>
    http.patch<ConnectionResponse>(`/v1/integrations/connections/${provider}`, data),
  disconnect: (provider: string) =>
    http.delete(`/v1/integrations/connections/${provider}`),
  sync: (provider: string) =>
    http.post<SyncLogResponse>(`/v1/integrations/connections/${provider}/sync`),
  /**
   * P0.1 Path A3 Day 4: per-event mute (Pro+).
   * Partial update of `config.event_subscriptions`. 400 with
   * "Professional or higher" detail on lower tiers.
   */
  updateEventSubscriptions: (
    provider: string,
    event_subscriptions: Record<string, boolean>,
  ) =>
    http.patch<ConnectionResponse>(
      `/v1/integrations/connections/${provider}/event-subscriptions`,
      { event_subscriptions },
    ),
  /**
   * P0.1 Path A3 Day 4: fire a synthetic 'svdy test message' through
   * the provider; bypasses event_subscriptions filtering. Returns
   * 200 with `sent: true/false` either way.
   */
  sendTest: (provider: string) =>
    http.post<{ provider: string; sent: boolean; detail: string | null }>(
      `/v1/integrations/connections/${provider}/test`,
    ),
  /**
   * 2026-05-25 Bug 5: start an OAuth install via XHR (instead of
   * relative-URL window.location, which falls through to the SPA's
   * not-found fallback because qutime.com / pureoa.com don't proxy
   * /v1/* to api.svdy.com).
   *
   * Returns the provider's authorize URL so the caller can do
   * window.location = result.auth_url to hand off to slack.com /
   * login.microsoftonline.com / etc.
   *
   * Backend re-checks plan tier and raises 402 license_required
   * on insufficient — the dialog upstream catches this and pops
   * the Upgrade dialog (svdy:upgrade:open custom event).
   */
  oauthStart: (provider: string, return_url: string) =>
    http.get<{ auth_url: string }>(
      `/v1/integrations/${provider}/oauth/start?response=json&return_url=${encodeURIComponent(return_url)}`,
    ),
};

// ---------------------------------------------------------------------------
// Sync Logs
// ---------------------------------------------------------------------------

export const syncLogsApi = {
  list: () => http.get<SyncLogResponse[]>('/v1/integrations/sync-logs'),
};

// ---------------------------------------------------------------------------
// Overview
// ---------------------------------------------------------------------------

export const integrationsOverviewApi = {
  get: () => http.get<IntegrationsOverview>('/v1/integrations/overview'),
};


// ---------------------------------------------------------------------------
// HRIS connectors (Workday, BambooHR) — provider-agnostic admin surface.
// Distinct from connectionsApi (IM messaging providers). The backend
// /v1/integrations/hris/* endpoints all take a {provider} path param; the
// provider's build_connection_config validates+encrypts the raw_config.
// ---------------------------------------------------------------------------

export type HrisProviderInfo = Schema<'HRISProviderInfo'>;
export type HrisConnectionResponse = Schema<'HRISConnectionResponse'>;
export type HrisTestConnectionResponse = Schema<'HRISTestConnectionResponse'>;

export const hrisApi = {
  providers: () =>
    http.get<Schema<'HRISProviderListResponse'>>('/v1/integrations/hris/providers'),
  connections: () =>
    http.get<Schema<'HRISConnectionListResponse'>>('/v1/integrations/hris/connections'),
  connect: (provider: string, raw_config: Record<string, string>) =>
    http.post<HrisConnectionResponse>('/v1/integrations/hris/connections', {
      provider,
      raw_config,
    }),
  disconnect: (provider: string) =>
    http.delete(`/v1/integrations/hris/connections/${provider}`),
  test: (provider: string) =>
    http.post<HrisTestConnectionResponse>(
      `/v1/integrations/hris/connections/${provider}/test`,
    ),
  syncNow: (provider: string) =>
    http.post<unknown>(`/v1/integrations/hris/connections/${provider}/sync-now`),
  // ADR-039 two-way (BambooHR): toggle outbound write-back (svdy -> HRIS).
  setOutbound: (provider: string, enabled: boolean) =>
    http.post<HrisConnectionResponse>(
      `/v1/integrations/hris/connections/${provider}/outbound`,
      { enabled },
    ),
  // ADR-039: run an outbound push now (svdy -> HRIS). dry_run previews.
  outboundSyncNow: (provider: string, dry_run = false) =>
    http.post<{ status: string; records_count: number; details: Record<string, unknown> }>(
      `/v1/integrations/hris/connections/${provider}/outbound-sync?dry_run=${dry_run}`,
    ),
};

// ---------------------------------------------------------------------------
// QuickBooks Online payroll export (ADR-041) — org-level OAuth2 + one-way
// export (svdy -> QuickBooks). Distinct from HRIS (directory sync) and
// messaging. Reuses the org-level IntegrationConnection; the export button
// pushes per-day TimeActivity rows for a date window.
// ---------------------------------------------------------------------------

export type QuickBooksConnectionStatus = Schema<'QuickBooksConnectionStatus'>;
export type QuickBooksExportResult = Schema<'QuickBooksExportResult'>;
export type QuickBooksExportHistoryItem = Schema<'QuickBooksExportHistoryItem'>;

export const quickbooksApi = {
  connection: () =>
    http.get<QuickBooksConnectionStatus>('/v1/integrations/quickbooks/connection'),
  disconnect: () => http.delete('/v1/integrations/quickbooks/connection'),
  oauthStart: (return_url: string) =>
    http.get<{ auth_url: string }>(
      `/v1/integrations/quickbooks/oauth/start?response=json&return_url=${encodeURIComponent(return_url)}`,
    ),
  export: (data: { start_date: string; end_date: string; dry_run?: boolean }) =>
    http.post<QuickBooksExportResult>('/v1/integrations/quickbooks/export', data),
  history: (limit = 20) =>
    http.get<{ items: QuickBooksExportHistoryItem[] }>(
      `/v1/integrations/quickbooks/export-history?limit=${limit}`,
    ),
};
